/**
 * PollingManager — 統一輪詢管理器（取代 RealtimeManager）
 *
 * 功能：
 * 1. 統一註冊/註銷輪詢任務
 * 2. 頁面不可見時自動暫停（省頻寬）
 * 3. 頁面回前景時立即執行一次 + 恢復排程
 * 4. 錯誤監控 + 連續失敗計數
 * 5. 支援手動強制刷新
 *
 * 使用方式：
 *   import { pollingManager } from '@shared/services/core/pollingManager'
 *
 *   const unsub = pollingManager.register({
 *     key: 'equipment:field123',
 *     fetchFn: async () => { ... },
 *     intervalMs: 15000,
 *   })
 *
 *   // 取消輪詢
 *   unsub()
 */

// =============================================
// Types
// =============================================

interface PollingConfig {
  /** 唯一識別鍵（同 key 會先取消舊的再註冊新的） */
  key: string
  /** 資料拉取函式 */
  fetchFn: () => Promise<void>
  /** 輪詢間隔（毫秒） */
  intervalMs: number
  /** 頁面不可見時是否暫停（預設 true） */
  pauseWhenHidden?: boolean
  /** 註冊後立即執行一次（預設 true） */
  immediate?: boolean
}

interface ManagedPolling {
  config: PollingConfig
  timer: ReturnType<typeof setInterval> | null
  /** 是否因頁面不可見而暫停中 */
  paused: boolean
  /** 連續失敗次數 */
  consecutiveErrors: number
  /** 是否正在執行 fetchFn */
  fetching: boolean
  /** 最後成功時間 */
  lastSuccessAt: number | null
}

type ErrorCallback = (key: string, error: unknown, consecutiveErrors: number) => void

// =============================================
// Constants
// =============================================

const PREFIX = '[PollingManager]'

// =============================================
// Manager
// =============================================

class PollingManager {
  private polls = new Map<string, ManagedPolling>()
  private errorCallbacks = new Set<ErrorCallback>()
  private visibilityHandler: (() => void) | null = null

  constructor() {
    this.setupVisibilityListener()
  }

  /**
   * 註冊一個輪詢任務。
   * 如果同 key 已存在，會先取消舊的。
   * 回傳取消輪詢的函式。
   */
  register(config: PollingConfig): () => void {
    if (this.polls.has(config.key)) {
      this.unregister(config.key)
    }

    const managed: ManagedPolling = {
      config,
      timer: null,
      paused: false,
      consecutiveErrors: 0,
      fetching: false,
      lastSuccessAt: null,
    }

    this.polls.set(config.key, managed)

    const immediate = config.immediate !== false

    if (immediate) {
      void this.executeFetch(config.key)
    }

    this.startTimer(config.key)

    return () => this.unregister(config.key)
  }

  /**
   * 取消輪詢
   */
  unregister(key: string): void {
    const managed = this.polls.get(key)
    if (!managed) return

    this.stopTimer(key)
    this.polls.delete(key)
  }

  /**
   * 取消所有輪詢
   */
  unregisterAll(): void {
    for (const key of this.polls.keys()) {
      this.unregister(key)
    }
  }

  /**
   * 手動強制刷新指定 key
   */
  async forceRefresh(key: string): Promise<void> {
    await this.executeFetch(key)
  }

  /**
   * 手動強制刷新所有
   */
  async forceRefreshAll(): Promise<void> {
    const keys = [...this.polls.keys()]
    await Promise.allSettled(keys.map((key) => this.executeFetch(key)))
  }

  /**
   * 監聽錯誤事件
   */
  onError(callback: ErrorCallback): () => void {
    this.errorCallbacks.add(callback)
    return () => this.errorCallbacks.delete(callback)
  }

  /**
   * 取得目前活躍的輪詢數
   */
  get activeCount(): number {
    return this.polls.size
  }

  /**
   * 取得所有輪詢的狀態
   */
  getStatus(): Record<string, { paused: boolean; consecutiveErrors: number; lastSuccessAt: number | null }> {
    const result: Record<string, { paused: boolean; consecutiveErrors: number; lastSuccessAt: number | null }> = {}
    for (const [key, managed] of this.polls) {
      result[key] = {
        paused: managed.paused,
        consecutiveErrors: managed.consecutiveErrors,
        lastSuccessAt: managed.lastSuccessAt,
      }
    }
    return result
  }

  /**
   * 是否所有輪詢都健康（無連續錯誤）
   */
  get allHealthy(): boolean {
    if (this.polls.size === 0) return true
    for (const managed of this.polls.values()) {
      if (managed.consecutiveErrors > 0) return false
    }
    return true
  }

  // =============================================
  // Internal
  // =============================================

  private async executeFetch(key: string): Promise<void> {
    const managed = this.polls.get(key)
    if (!managed) return

    // 避免重疊執行
    if (managed.fetching) return
    managed.fetching = true

    try {
      await managed.config.fetchFn()
      // fetchFn 期間可能被 unregister，檢查是否仍存在
      if (!this.polls.has(key)) return
      managed.consecutiveErrors = 0
      managed.lastSuccessAt = Date.now()
    } catch (error: unknown) {
      if (!this.polls.has(key)) return
      managed.consecutiveErrors++
      this.notifyError(key, error, managed.consecutiveErrors)
    } finally {
      managed.fetching = false
    }
  }

  private startTimer(key: string): void {
    const managed = this.polls.get(key)
    if (!managed) return

    this.stopTimer(key)

    managed.timer = setInterval(() => {
      if (!managed.paused) {
        void this.executeFetch(key)
      }
    }, managed.config.intervalMs)
  }

  private stopTimer(key: string): void {
    const managed = this.polls.get(key)
    if (!managed) return

    if (managed.timer !== null) {
      clearInterval(managed.timer)
      managed.timer = null
    }
  }

  private notifyError(key: string, error: unknown, consecutiveErrors: number): void {
    for (const cb of this.errorCallbacks) {
      try {
        cb(key, error, consecutiveErrors)
      } catch (e) {
        console.error(`${PREFIX} error callback error:`, e)
      }
    }
  }

  private setupVisibilityListener(): void {
    if (typeof document === 'undefined') return

    this.visibilityHandler = () => {
      const isVisible = document.visibilityState === 'visible'

      for (const [key, managed] of this.polls) {
        const pauseWhenHidden = managed.config.pauseWhenHidden !== false

        if (!pauseWhenHidden) continue

        if (isVisible && managed.paused) {
          // 回到前景：立即拉一次 + 恢復排程
          managed.paused = false
          void this.executeFetch(key)
        } else if (!isVisible && !managed.paused) {
          // 進入背景：暫停
          managed.paused = true
        }
      }
    }

    document.addEventListener('visibilitychange', this.visibilityHandler)
  }

  /**
   * 銷毀 manager
   */
  destroy(): void {
    this.unregisterAll()
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler)
      this.visibilityHandler = null
    }
    this.errorCallbacks.clear()
  }
}

// Singleton
export const pollingManager = new PollingManager()
export type { PollingConfig, ErrorCallback }
export default pollingManager
